import React, { useMemo, useCallback } from 'react';
import FilterOption, { FilterOptionProps } from '../FilterOption';
import CancelIcon from '@material-ui/icons/Cancel';
import {
  FilterContainer,
  FilterTitle,
  ChipsContainer,
  StyledChip,
  SectionTitle,
  EmptyMessage,
} from './SidebarFilters.styles';
import { Box, IconButton } from '@material-ui/core';
import FirstPageIcon from '@material-ui/icons/FirstPage';

export interface FilterOptionData extends Omit<FilterOptionProps, 'onChange'> {
  id: string;
  label: string;
  qty: number;
}

export interface SelectableFilterProps {
  options: FilterOptionData[];
  selectedOptions?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  onClose?: () => void;
}

const SidebarFilters = ({
  options = [],
  selectedOptions = [],
  onSelectionChange,
  onClose,
}: SelectableFilterProps) => {
  const hasActiveFilters = selectedOptions.length > 0;

  const sortedOptions = useMemo(
    () => [...options].sort((a, b) => b.qty - a.qty),
    [options],
  );

  const removeApiTypeFilter = useCallback(
    (apiTypeToRemove: string) => {
      onSelectionChange?.(selectedOptions.filter(id => id !== apiTypeToRemove));
    },
    [onSelectionChange, selectedOptions],
  );

  const handleOptionChange = useCallback(
    (optionId: string, isChecked: boolean) => {
      if (!onSelectionChange) return;
      onSelectionChange(
        isChecked
          ? [...selectedOptions, optionId]
          : selectedOptions.filter(id => id !== optionId),
      );
    },
    [onSelectionChange, selectedOptions],
  );

  const maxValue = useMemo(
    () => Math.max(...sortedOptions.map(option => option.qty), 0),
    [sortedOptions],
  );

  return (
    <FilterContainer>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: hasActiveFilters ? 8 : 24,
        }}
      >
        <FilterTitle variant="h6">Filter in Categories</FilterTitle>
        <IconButton onClick={onClose}>
          <FirstPageIcon color="primary" />
        </IconButton>
      </Box>

      {hasActiveFilters && (
        <ChipsContainer>
          {selectedOptions.map(apiType => (
            <StyledChip
              key={apiType}
              label={apiType}
              variant="outlined"
              onDelete={() => removeApiTypeFilter(apiType)}
              deleteIcon={<CancelIcon />}
              size="small"
            />
          ))}
        </ChipsContainer>
      )}

      <SectionTitle variant="body1">Product Types</SectionTitle>

      <div>
        {sortedOptions.length > 0 ? (
          sortedOptions.map(option => (
            <FilterOption
              key={option.id}
              label={option.label}
              value={option.qty}
              maxValue={maxValue}
              checked={selectedOptions.includes(option.id)}
              onChange={isChecked => handleOptionChange(option.id, isChecked)}
            />
          ))
        ) : (
          <EmptyMessage color="secondary">No options available</EmptyMessage>
        )}
      </div>
    </FilterContainer>
  );
};

export default SidebarFilters;
